<template>
  <span
    class="light-dot"
    :class="[`light-dot--${modifier}`, { 'light-dot--compact': size === 'compact' }]"
    :title="title ?? label"
  >{{ letter }}</span>
</template>

<script setup>
import { computed } from 'vue';

// One condition light, as a lettered puck.
//
// It took `color`, `fg` and `ring` as props — three colors the CALLER had to
// know, which is how the five auction-light hues ended up hard-coded in two
// different components. It takes a `code` now and reads the brand's own
// `auctionLight` tokens, so the disclosure colors have one definition.
//
// `unset` is a real code, not a missing one: a lot whose lights have not been
// entered shows a neutral "?" rather than nothing, because "no lights" and "not
// inspected yet" are different facts to a clerk.
defineOptions({ name: 'LightDot' });

const props = defineProps({
  // R | G | Y | B | W | unset
  code: {
    type: String,
    required: true,
    validator: (value) => ['R', 'G', 'Y', 'B', 'W', 'unset'].includes(value),
  },
  // Overrides the light's own name in the tooltip.
  title: { type: String, default: null },
  // `default` is the console's puck, read across a room. `compact` is the same
  // puck sized to sit inside a line of type, which is what the marketplace hero
  // needs — see the `compact` block in componentTokens.mjs for why that is a
  // size on this component rather than a second one beside it.
  size: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'compact'].includes(value),
  },
});

// Industry-standard dealer disclosure lights. The label doubles as the default
// tooltip and as the editor's caption.
const LIGHTS = {
  R: { letter: 'R', label: 'Red', modifier: 'red' },
  G: { letter: 'G', label: 'Green', modifier: 'green' },
  Y: { letter: 'Y', label: 'Yellow', modifier: 'yellow' },
  B: { letter: 'B', label: 'Blue', modifier: 'blue' },
  W: { letter: 'W', label: 'White', modifier: 'white' },
  unset: { letter: '?', label: 'Lights not yet set', modifier: 'unset' },
};

const light = computed(() => LIGHTS[props.code] ?? LIGHTS.unset);
const letter = computed(() => light.value.letter);
const label = computed(() => light.value.label);
const modifier = computed(() => light.value.modifier);
</script>

<style scoped>
.light-dot {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--p-light-dot-border-radius);
  border-width: var(--p-light-dot-border-width);
  border-style: solid;
  font-family: var(--p-light-dot-font-family);
  font-size: var(--p-light-dot-font-size);
  font-weight: var(--p-light-dot-font-weight);
  letter-spacing: var(--p-light-dot-letter-spacing);
  line-height: 1;
}

.light-dot--red {
  background: var(--p-light-dot-red-background);
  color: var(--p-light-dot-red-color);
  border-color: var(--p-light-dot-red-border-color);
  box-shadow: var(--p-light-dot-red-box-shadow);
}

.light-dot--green {
  background: var(--p-light-dot-green-background);
  color: var(--p-light-dot-green-color);
  border-color: var(--p-light-dot-green-border-color);
  box-shadow: var(--p-light-dot-green-box-shadow);
}

.light-dot--yellow {
  background: var(--p-light-dot-yellow-background);
  color: var(--p-light-dot-yellow-color);
  border-color: var(--p-light-dot-yellow-border-color);
  box-shadow: var(--p-light-dot-yellow-box-shadow);
}

.light-dot--blue {
  background: var(--p-light-dot-blue-background);
  color: var(--p-light-dot-blue-color);
  border-color: var(--p-light-dot-blue-border-color);
  box-shadow: var(--p-light-dot-blue-box-shadow);
}

.light-dot--white {
  background: var(--p-light-dot-white-background);
  color: var(--p-light-dot-white-color);
  border-color: var(--p-light-dot-white-border-color);
  box-shadow: var(--p-light-dot-white-box-shadow);
}

.light-dot--unset {
  background: var(--p-light-dot-unset-background);
  color: var(--p-light-dot-unset-color);
  border-color: var(--p-light-dot-unset-border-color);
}

/* After the colors, deliberately: every one of them sets a box-shadow, and at
   one class each the later rule is what decides. */
.light-dot--compact {
  width: 15px;
  height: 15px;
  border-width: var(--p-light-dot-compact-border-width);
  font-size: var(--p-light-dot-compact-font-size);
  box-shadow: var(--p-light-dot-compact-box-shadow);
}
</style>
